import React, { createContext, useState, useEffect, useContext, type JSX } from 'react';
import { IWishlist } from '@monofront/types';
import { WishlistService } from '../services';
import { IAuthState } from './createAuthContext';
import { Channel } from '../constants';

interface IWishlistContext {
  wishlists: IWishlist[];
  isLoading: boolean;
  createWishlist: (wishlistName: string, wishlistProducts?) => Promise<string>;
  deleteWishlist: (wishlistUid: string) => Promise<void>;
  renameWishlist: (wishlistUid: string, wishlistName: string) => Promise<void>;
  addProductToWishlist: (
    wishlistUid: string,
    objectId: string,
  ) => Promise<void>;
  deleteProductFromWishlist: (
    wishlistUid: string,
    objectId: string,
  ) => Promise<void>;
  deleteAllProductsFromWishlist: (wishlistUid: string) => Promise<void>;
  isProductInOneWishlist: (variantId: string) => boolean;
}

interface IWishlistContextCreator {
  /** Context provider component */
  WishlistProvider: ({
    children,
    channel,
  }: {
    children: React.ReactNode;
    channel: Channel;
  }) => JSX.Element;
  /** Context consumer component */
  useWishlist: () => IWishlistContext;
}

export const createWishlistContext = (
  wishlistService: WishlistService,
  useAuthentication: () => Partial<IAuthState>,
): IWishlistContextCreator => {
  const WishlistContext = createContext<IWishlistContext>({
    wishlists: [],
    isLoading: true,
    // useless mandatory declarations...
    createWishlist: async () => 'wishlistUid',
    deleteWishlist: async () => undefined,
    renameWishlist: async () => undefined,
    addProductToWishlist: async () => undefined,
    deleteProductFromWishlist: async () => undefined,
    deleteAllProductsFromWishlist: async () => undefined,
    isProductInOneWishlist: () => false,
  });

  const WishlistProvider = ({ children, channel }) => {
    const { authUser } = useAuthentication();
    const [wishlists, setWishlists] = useState<IWishlist[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
      if (!authUser) {
        setWishlists([]);

        return;
      }

      const unlisten = wishlistService.listenToWishlist(
        authUser.uid,
        channel,
        setWishlists,
        setIsLoading,
      );

      return () => unlisten();
    }, [authUser]);

    const createWishlist = async (
      wishlistName: string,
      wishlistProducts: string[],
    ): Promise<string> => {
      return await wishlistService.createWishlist(
        authUser!.uid,
        wishlistName,
        wishlistProducts,
      );
    };

    const deleteWishlist = async (wishlistUid: string): Promise<void> => {
      await wishlistService.deleteWishlist(authUser!.uid, wishlistUid);
    };

    const renameWishlist = async (
      wishlistUid: string,
      wishlistName: string,
    ): Promise<void> => {
      await wishlistService.renameWishlist(
        authUser!.uid,
        wishlistUid,
        wishlistName,
      );
    };

    const addProductToWishlist = async (
      wishlistUid: string,
      objectId: string,
    ): Promise<void> => {
      const currentProductsInWishlist =
        wishlists
          .find(({ id }) => id === wishlistUid)
          ?.products.map(({ objectID }) => objectID) || [];

      await wishlistService.addProductToWishlist(
        authUser!.uid,
        wishlistUid,
        currentProductsInWishlist,
        objectId,
      );
    };

    const deleteProductFromWishlist = async (
      wishlistUid: string,
      objectId: string,
    ): Promise<void> => {
      const currentProductsInWishlist =
        wishlists
          .find(({ id }) => id === wishlistUid)
          ?.products.map(({ objectID }) => objectID) || [];

      await wishlistService.deleteProductFromWishlist(
        authUser!.uid,
        wishlistUid,
        currentProductsInWishlist,
        objectId,
      );
    };

    const deleteAllProductsFromWishlist = async (
      wishlistUid: string,
    ): Promise<void> => {
      await wishlistService.deleteAllProductsFromWishlist(
        authUser!.uid,
        wishlistUid,
      );
    };

    const isProductInOneWishlist = (variantId: string) =>
      wishlists
        .map((wishlist) => wishlist.products)
        .flat()
        .some((product) => product.objectID === variantId);

    return (
      <WishlistContext.Provider
        value={{
          wishlists,
          isLoading,
          createWishlist,
          deleteWishlist,
          renameWishlist,
          addProductToWishlist,
          deleteProductFromWishlist,
          deleteAllProductsFromWishlist,
          isProductInOneWishlist,
        }}
      >
        {children}
      </WishlistContext.Provider>
    );
  };

  return { WishlistProvider, useWishlist: () => useContext(WishlistContext) };
};
